<template>
  <div :class="['template-designer-v2', { 'embedded-mode': embedded }]">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="toolbar-left">
        <a-button @click="handleReset" icon="undo">
          恢复默认模板
        </a-button>
      </div>
      
      <div class="toolbar-center">
        <!-- 纸张类型按钮组 -->
        <a-button-group>
          <a-button 
            v-for="(paper, key) in paperTypes" 
            :key="key"
            :type="curPaperType === key ? 'primary' : 'default'"
            @click="setPaper(key, paper)"
          >
            {{ key }}
          </a-button>
          <a-button :type="curPaperType === 'custom' ? 'primary' : 'default'" @click="showCustomPaperModal">
            自定义
          </a-button>
        </a-button-group>
        
        <!-- 缩放控制 -->
        <div class="zoom-control">
          <a-button icon="minus" @click="changeScale(false)" :disabled="scaleValue <= scaleMin" />
          <span class="scale-display">{{ Math.round(scaleValue * 100) }}%</span>
          <a-button icon="plus" @click="changeScale(true)" :disabled="scaleValue >= scaleMax" />
        </div>
      </div>
      
      <div class="toolbar-right">
        <a-button icon="eye" @click="handlePreview">打印预览</a-button>
        <a-button icon="reload" @click="handleRefresh">刷新</a-button>
        <a-button icon="select" @click="selectAll">全选</a-button>
        <a-button type="primary" icon="save" @click="handleSave">保存</a-button>
        <a-button icon="delete" @click="handleClear">清空</a-button>
        <a-button icon="code" @click="showJsonModal">模板JSON</a-button>
        <a-button v-if="!embedded" icon="close" @click="handleBack">关闭</a-button>
      </div>
    </div>
    
    <!-- 自定义纸张弹窗 -->
    <a-modal
      :visible="customPaperModalVisible"
      title="自定义纸张大小"
      :footer="null"
      width="400px"
      @cancel="customPaperModalVisible = false"
      style="top: 200px;"
    >
      <div class="custom-paper-form">
        <div class="form-item">
          <label>宽度(mm)</label>
          <a-input-number v-model="paperWidth" :min="20" :max="1000" style="width: 150px;" />
        </div>
        <div class="form-item">
          <label>高度(mm)</label>
          <a-input-number v-model="paperHeight" :min="20" :max="1000" style="width: 150px;" />
        </div>
        <div class="form-actions">
          <a-button @click="customPaperModalVisible = false">取消</a-button>
          <a-button type="primary" @click="applyCustomPaper">确定</a-button>
        </div>
      </div>
    </a-modal>
    
    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 左侧字段面板 -->
      <div class="left-panel">
        <div class="panel-header">可用字段</div>
        <div class="panel-content">
          <!-- hiprint 字段容器 -->
          <div class="hiprintEpContainer"></div>
          
          <!-- 加载状态 -->
          <div v-if="!fieldPanelLoaded" class="loading-hint">
            <a-spin size="small" /> 加载字段中...
          </div>
        </div>
      </div>
      
      <!-- 中间画布区 -->
      <div class="center-panel">
        <!-- 标尺 -->
        <div class="ruler"></div>
        
        <!-- hiprint 设计画布 -->
        <div id="hiprint-printTemplate" class="hiprint-printTemplate"></div>
        
        <!-- hiprint分页容器（隐藏） -->
        <div class="hiprint-printPagination" style="display: none;"></div>
        
        <!-- 空状态 -->
        <div v-if="!templateLoaded" class="empty-state">
          <a-empty description="正在加载模板..." />
        </div>
      </div>
      
      <!-- 右侧属性面板 -->
      <div class="right-panel">
        <div class="panel-header">属性设置</div>
        <div class="panel-content">
          <!-- hiprint 属性设置容器 -->
          <div id="PrintElementOptionSetting" class="PrintElementOptionSetting"></div>
          
          <!-- 提示 -->
          <div v-if="!hasSelectedElement" class="selection-hint">
            <a-icon type="info-circle" /> 点击画布上的元素以编辑属性
          </div>
        </div>
      </div>
    </div>
    
    <!-- JSON编辑弹窗 -->
    <a-modal
      :visible="jsonModalVisible"
      title="模板JSON编辑"
      width="700px"
      :footer="null"
      @cancel="jsonModalVisible = false"
    >
      <div class="json-modal-content">
        <div class="json-toolbar">
          <a-switch v-model="jsonCompressed" checked-children="压缩" un-checked-children="格式化" />
          <a-button type="link" size="small" @click="formatJson">格式化</a-button>
          <a-button type="link" size="small" @click="compressJson">压缩</a-button>
        </div>
        <a-textarea
          v-model="templateJsonText"
          :rows="20"
          placeholder="模板JSON内容"
        />
        <div class="json-modal-footer">
          <a-button @click="jsonModalVisible = false">取消</a-button>
          <a-button type="primary" @click="applyJson">应用</a-button>
        </div>
      </div>
    </a-modal>
    
    <!-- 打印预览对话框 -->
    <a-modal
      :visible="previewVisible"
      title="打印预览"
      width="90%"
      :footer="null"
      @cancel="previewVisible = false"
      wrapClassName="preview-modal"
    >
      <div class="preview-container">
        <!-- 左侧：设置 -->
        <div class="preview-settings">
          <div class="settings-section">
            <h4>设置</h4>
            <a-divider style="margin: 8px 0;" />
            <div class="setting-item">
              <span>纸张大小</span>
              <a-select v-model="previewPaperSize" style="width: 100px;">
                <a-select-option value="A4">A4</a-select-option>
                <a-select-option value="A5">A5</a-select-option>
                <a-select-option value="B5">B5</a-select-option>
                <a-select-option value="Letter">Letter</a-select-option>
              </a-select>
            </div>
            <div class="setting-item">
              <span>方向</span>
              <a-radio-group v-model="previewOrientation">
                <a-radio value="portrait">纵向</a-radio>
                <a-radio value="landscape">横向</a-radio>
              </a-radio-group>
            </div>
            <div class="setting-item">
              <span>缩放</span>
              <a-slider v-model="previewZoom" :min="50" :max="200" style="width: 150px;" />
            </div>
          </div>
          
          <div class="preview-actions">
            <a-button type="primary" icon="printer" block @click="doPrintFromPreview">打印</a-button>
          </div>
        </div>
        
        <!-- 右侧：预览内容 -->
        <div class="preview-content-wrapper">
          <div class="preview-toolbar">
            <span>共 1 页</span>
            <a-divider type="vertical" />
            <a-button size="small" icon="reload" @click="refreshPreview">刷新预览</a-button>
          </div>
          <div class="preview-area" ref="previewArea">
            <div 
              class="preview-paper"
              :style="{ transform: `scale(${previewZoom / 100})`, transformOrigin: 'top center' }"
            >
              <div v-html="previewHtml"></div>
            </div>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { registerAllBillTypes, registerBillType, getProvidersArr, findProviderByType, initHiprint } from '@/services/hiprint-register'
import HiprintService from '@/services/hiprint-service'
import { getList, saveBillPrintTemplate, getStructuredFields, loadTemplateWithFallback } from '@/api/plugin/print-template'
import { 
  getDefaultTemplateByName, 
  getDefaultTemplateByCode, 
  getBillTypeName, 
  getBillTypeCode,
  getAllBillTypes,
  codeToNameMap 
} from '@/assets/print_template'
import { getBillTypeFromNo, normalizeBillType } from '@/utils/billTypeUtils'

export default {
  name: 'TemplateDesignerV2',
  
  props: {
    billType: {
      type: String,
      default: ''
    },
    billTypeName: {
      type: String,
      default: ''
    },
    // 新增：单据编号（用于智能识别类型）
    billNo: {
      type: String,
      default: ''
    },
    templateId: {
      type: [String, Number],
      default: null
    },
    // 新增：真实单据数据（用于预览）
    billData: {
      type: Object,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      // hiprint实例
      template: null,
      
      // 当前纸张配置
      curPaper: {
        type: 'A4',
        width: 210,
        height: 296.6
      },
      
      // 纸张类型
      paperTypes: {
        'A4': { width: 210, height: 296.6 },
        '二等分': { width: 241, height: 140 },
        '三等分': { width: 241, height: 93.3 },
        '四等分': { width: 241, height: 70 }
      },
      
      // 自定义纸张
      customPaperModalVisible: false,
      paperWidth: 210,
      paperHeight: 296.6,
      
      // 缩放
      scaleValue: 1,
      scaleMin: 0.5,
      scaleMax: 5,
      
      // 当前单据类型
      selectedBillType: '',
      
      // 模板相关
      selectedTemplate: null,
      templateTenantId: null,  // 当前加载模板的租户ID（null或0为系统模板）
      currentTemplateName: '',
      templateList: [],
      
      // UI状态
      previewVisible: false,
      previewPaperSize: 'A4',
      previewOrientation: 'portrait',
      previewZoom: 100,
      previewHtml: '',
      jsonModalVisible: false,
      jsonCompressed: true,
      templateJsonText: '',
      hasSelectedElement: false,
      
      // 加载状态
      templateLoaded: false,
      fieldPanelLoaded: false
    }
  },
  
  computed: {
    curPaperType() {
      return this.curPaper.type
    }
  },
  
  watch: {
    billType: {
      handler(newVal, oldVal) {
        console.log('[TemplateDesignerV2] billType changed:', oldVal, '->', newVal)
        if (newVal && newVal !== oldVal) {
          this.reinitialize()
        }
      },
      immediate: false
    }
  },
  
  created() {
    console.log('[TemplateDesignerV2] created, billType:', this.billType, 'billTypeName:', this.billTypeName)
    this.initBillType()
  },
  
  mounted() {
    console.log('[TemplateDesignerV2] mounted')
    this.$nextTick(() => {
      this.initDesigner()
    })
  },
  
  beforeDestroy() {
    console.log('[TemplateDesignerV2] beforeDestroy')
    if (this.template) {
      try {
        if (this.template.destroy) {
          this.template.destroy()
        } else {
          this.template.clear()
        }
        this.template = null
      } catch (e) {
        console.warn('清理设计器失败', e)
      }
    }
  },
  
  methods: {
    /**
     * 重新初始化
     */
    async reinitialize() {
      console.log('[TemplateDesignerV2] reinitialize')
      
      // 销毁旧模板
      if (this.template) {
        try {
          if (this.template.destroy) {
            this.template.destroy()
          }
          this.template = null
        } catch (e) {
          console.warn('销毁旧模板失败', e)
        }
      }
      
      // 重置状态
      this.templateLoaded = false
      this.fieldPanelLoaded = false
      this.hasSelectedElement = false
      
      // 清空画布
      const container = document.getElementById('hiprint-printTemplate')
      if (container) {
        container.innerHTML = ''
      }
      
      // 重新初始化
      this.initBillType()
      await this.initDesigner()
    },
    
    /**
     * 初始化单据类型
     */
    initBillType() {
      let billTypeCode = this.billType
      
      console.log('[TemplateDesignerV2] initBillType, raw billType:', billTypeCode, 'billNo:', this.billNo)
      
      // 1. 如果有 billNo，优先从 billNo 识别
      if (!billTypeCode && this.billNo) {
        billTypeCode = getBillTypeFromNo(this.billNo)
        console.log('[TemplateDesignerV2] identified from billNo:', billTypeCode)
      }
      
      // 2. 如果没有，尝试转换
      if (billTypeCode && !codeToNameMap[billTypeCode]) {
        console.log('[TemplateDesignerV2] billType not in codeToNameMap, trying to convert')
        const code = getBillTypeCode(billTypeCode)
        console.log('[TemplateDesignerV2] converted code:', code)
        if (code !== billTypeCode) {
          billTypeCode = code
        }
      }
      
      // 3. 规范化
      if (billTypeCode) {
        billTypeCode = normalizeBillType(billTypeCode)
      }
      
      this.selectedBillType = billTypeCode || 'QGD'
      console.log('[TemplateDesignerV2] selectedBillType:', this.selectedBillType)
      
      // 如果有模板ID，加载对应模板
      if (this.templateId) {
        this.selectedTemplate = Number(this.templateId)
      }
    },
    
    /**
     * 初始化设计器
     * 使用 Provider 模式初始化 hiprint
     */
    async initDesigner() {
      console.log('[TemplateDesignerV2] initDesigner start, selectedBillType:', this.selectedBillType, 'billType prop:', this.billType)
      
      try {
        // 验证 hiprint 是否可用
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (!hiprint) {
          console.error('[TemplateDesignerV2] hiprint not found!')
          this.$message.error('hiprint未加载，请刷新页面重试')
          return
        }
        
        // 初始化 hiprint 模块引用
        initHiprint(hiprint)
        
        console.log('[TemplateDesignerV2] hiprint available:', !!hiprint, 'PrintTemplate:', !!hiprint.PrintTemplate)
        
        // 查找匹配的 provider
        const providers = getProvidersArr()
        let matchedProvider = null
        
        // 尝试用 code 匹配 (如 "LSCK")
        matchedProvider = providers.find(p => p.value === this.selectedBillType)
        
        // 如果没找到，尝试用 type (中文全名) 匹配
        if (!matchedProvider) {
          matchedProvider = providers.find(p => p.type === this.billType)
        }
        
        if (!matchedProvider) {
          console.error('[TemplateDesignerV2] 未找到 Provider:', this.selectedBillType, this.billType)
          this.$message.error('未找到对应的单据类型配置')
          return
        }
        
        console.log('[TemplateDesignerV2] 使用 Provider:', matchedProvider.name, matchedProvider.value)
        
        // 初始化 hiprint (使用 provider)
        // 注意：hiprint.init 会调用 provider 的 addElementTypes 方法，并传入内部实例
        if (typeof hiprint.init === 'function') {
            hiprint.init({ 
                providers: [matchedProvider.f],
                socketUrl: false 
            })
            console.log('[TemplateDesignerV2] hiprint.init 成功，已注册元素类型')
        } else {
            console.error('[TemplateDesignerV2] hiprint.init 不存在！')
            this.$message.error('hiprint 初始化失败，请刷新页面重试')
            return
        }
        
        // 获取模板数据 - 使用两级回退
        let templateJsonData = null
        let loadedTemplateId = null
        let loadedTemplateName = null
        
        try {
          // 使用新的两级回退加载函数，返回 { id, templateName, templateJson }
          const userInfo = this.$store && (this.$store.getters.userInfo || (this.$store.state.user && this.$store.state.user.info))
          const tenantId = userInfo ? userInfo.tenantId : null
          const result = await loadTemplateWithFallback(this.selectedBillType, tenantId)
          console.log('[TemplateDesignerV2] loadTemplateWithFallback result:', result ? 'success' : 'empty')
          
          if (result) {
            // 提取模板ID（用于后续保存时更新）
            if (result.id) {
              loadedTemplateId = result.id
              this.selectedTemplate = result.id
            }
            // 提取模板的租户ID（用于判断保存时是新增还是更新）
            // tenantId 为 0 或 undefined 表示系统模板，保存时需要新增
            if (result.tenantId !== undefined) {
              this.templateTenantId = result.tenantId
            }
            console.log('[TemplateDesignerV2] 加载模板 tenantId:', this.templateTenantId, 'id:', result.id)
            // 提取模板名称
            if (result.templateName) {
              loadedTemplateName = result.templateName
            }
            // 提取模板JSON
            templateJsonData = result.templateJson || result
          }
        } catch (err) {
          console.warn('[TemplateDesignerV2] loadTemplateWithFallback failed:', err.message)
        }
        
        // 如果两级回退失败，使用旧方法
        if (!templateJsonData || !templateJsonData.panels) {
          // 1. 先尝试通过中文全名获取
          if (this.billType) {
            templateJsonData = getDefaultTemplateByName(this.billType)
            console.log('[TemplateDesignerV2] 1. getDefaultTemplateByName("' + this.billType + '"):', !!templateJsonData)
          }
          
          // 2. 如果没有，尝试通过 code 获取
          if (!templateJsonData && this.selectedBillType) {
            templateJsonData = getDefaultTemplateByCode(this.selectedBillType)
            console.log('[TemplateDesignerV2] 2. getDefaultTemplateByCode("' + this.selectedBillType + '"):', !!templateJsonData)
          }
          
          // 3. 如果还是没有，尝试通过 billTypeName prop 获取
          if (!templateJsonData && this.billTypeName) {
            templateJsonData = getDefaultTemplateByName(this.billTypeName)
            console.log('[TemplateDesignerV2] 3. getDefaultTemplateByName("' + this.billTypeName + '"):', !!templateJsonData)
          }
        }
        
        if (!templateJsonData) {
          console.warn('[TemplateDesignerV2] No template found! will use empty template')
          this.$message.warning('未找到默认模板，将使用空模板')
          templateJsonData = { panels: [] }
        } else {
          console.log('[TemplateDesignerV2] Template found successfully, panels:', templateJsonData.panels && templateJsonData.panels.length)
          // 设置模板名称：优先使用加载的名称，否则使用 Provider 名称
          this.currentTemplateName = loadedTemplateName || matchedProvider.name + '（默认）'
        }
        
        // 初始化 hiprint 设计器
        this.initHiprintDesigner(templateJsonData)
        
      } catch (error) {
        console.error('[TemplateDesignerV2] 初始化设计器失败', error)
        this.$message.error('初始化设计器失败: ' + error.message)
      }
    },
    
    /**
     * 解析模板JSON
     */
    parseTemplateJson(templateJson) {
      if (!templateJson) return null
      try {
        return typeof templateJson === 'string' ? JSON.parse(templateJson) : templateJson
      } catch (e) {
        console.warn('解析模板JSON失败', e)
        return null
      }
    },
    
    /**
     * 加载模板列表
     */
    async loadTemplateList() {
      try {
        // 直接使用 billType code 查询后端（如 CGDD）
        const res = await getList({ billType: this.selectedBillType, size: 100 })
        
        if (res && res.code === 200) {
          this.templateList = (res.data && (res.data.list || res.data.records)) || []
        }
      } catch (error) {
        console.warn('加载模板列表失败', error)
        this.templateList = []
      }
    },
    
    /**
     * 初始化hiprint设计器
     */
    initHiprintDesigner(templateData) {
      console.log('[TemplateDesignerV2] initHiprintDesigner, templateData:', !!templateData)
      
      const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
      
      if (!hiprint || !hiprint.PrintTemplate) {
        console.error('[TemplateDesignerV2] hiprint.PrintTemplate not found!')
        this.$message.error('hiprint未加载，请刷新页面重试')
        return
      }
      
      try {
        console.log('[TemplateDesignerV2] Creating PrintTemplate instance...')
        
        // 创建PrintTemplate实例
        this.template = new hiprint.PrintTemplate({
          template: templateData || { panels: [] },
          dataMode: 1,
          history: false,
          onDataChanged: () => {},
          settingContainer: '#PrintElementOptionSetting',
          paginationContainer: '.hiprint-printPagination'
        })
        
        console.log('[TemplateDesignerV2] PrintTemplate instance created')
        
        // 进入设计模式
        this.template.design('#hiprint-printTemplate')
        console.log('[TemplateDesignerV2] design() called')
        
        // 标记模板已加载
        this.$nextTick(() => {
          this.templateLoaded = true
          console.log('[TemplateDesignerV2] templateLoaded = true')
          
          // 构建字段面板
          this.buildFieldPanel()
          
          // 设置选择监听
          this.setupElementSelection()
          
          // 自动选中第一个元素
          this.autoSelectFirstElement()
        })
        
      } catch (error) {
        console.error('[TemplateDesignerV2] 初始化hiprint设计器失败', error)
        this.$message.error('初始化hiprint设计器失败: ' + error.message)
      }
    },
    
    /**
     * 构建字段面板 - 使用 Provider 方式
     */
    buildFieldPanel() {
      console.log('[TemplateDesignerV2] buildFieldPanel, selectedBillType:', this.selectedBillType)
      
      const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
      
      if (!hiprint) {
        console.warn('[TemplateDesignerV2] hiprint not available for buildFieldPanel')
        return
      }
      
      const container = document.querySelector('.hiprintEpContainer')
      if (!container) {
        console.warn('[TemplateDesignerV2] .hiprintEpContainer not found')
        return
      }
      
      // 清空容器
      container.innerHTML = ''
      
      try {
        // 查找 provider
        const providers = getProvidersArr()
        let matchedProvider = providers.find(p => p.value === this.selectedBillType)
        
        if (!matchedProvider) {
          matchedProvider = providers.find(p => p.type === this.billType)
        }
        
        if (matchedProvider) {
          console.log('[TemplateDesignerV2] Found provider:', matchedProvider.name, matchedProvider.value)
          
          // 使用 hiprint 内置的方法构建字段面板
          // 注意：这里不再需要再次调用 addElementTypes，因为已经在 initDesigner 中通过 hiprint.init 完成了
          if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
            console.log('[TemplateDesignerV2] Using PrintElementTypeManager.build with key:', matchedProvider.value)
            hiprint.PrintElementTypeManager.build('.hiprintEpContainer', matchedProvider.value)
            this.fieldPanelLoaded = true
          } else {
            console.warn('[TemplateDesignerV2] PrintElementTypeManager.build not available')
            this.renderSimpleFieldPanel(container)
          }
        } else {
          console.warn('[TemplateDesignerV2] No provider found for:', this.selectedBillType, this.billType)
          this.renderSimpleFieldPanel(container)
        }
      } catch (error) {
        console.error('[TemplateDesignerV2] Error building field panel:', error)
        this.renderSimpleFieldPanel(container)
      }
    },
    
    /**
     * 回退到默认的字段面板构建方法
     */
    buildFieldPanelDefault(hiprint, container) {
      console.log('[TemplateDesignerV2] Using default field panel method')
      
      try {
        // 方法1: 使用 PrintElementTypeManager.build
        if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
          console.log('[TemplateDesignerV2] Using PrintElementTypeManager.build')
          hiprint.PrintElementTypeManager.build('.hiprintEpContainer', 'defaultModule')
          this.fieldPanelLoaded = true
        } 
        // 方法2: 使用 addPrintElementTypes 后手动渲染
        else if (typeof hiprint.addPrintElementTypes === 'function') {
          console.log('[TemplateDesignerV2] Using addPrintElementTypes approach')
          hiprint.addPrintElementTypes(this.selectedBillType, [])
          
          // 尝试使用 buildFieldPanelForTemplate
          if (this.template && typeof this.template.buildFieldPanelForTemplate === 'function') {
            this.template.buildFieldPanelForTemplate('.hiprintEpContainer', this.selectedBillType)
          }
          this.fieldPanelLoaded = true
        } 
        // 方法3: 查找其他可用方法
        else {
          console.warn('[TemplateDesignerV2] No known method for building field panel')
          console.log('[TemplateDesignerV2] Available hiprint methods:', Object.keys(hiprint).filter(k => typeof hiprint[k] === 'function'))
          // 渲染一个简单的字段面板
          this.renderSimpleFieldPanel(container)
        }
      } catch (error) {
        console.error('[TemplateDesignerV2] buildFieldPanelDefault error:', error)
        this.renderSimpleFieldPanel(container)
      }
    },
    
    /**
     * 将后端字段注册到 hiprint
     */
    registerFieldsToHiprint(hiprint, fieldsData) {
      console.log('[TemplateDesignerV2] registerFieldsToHiprint')
      
      try {
        // 准备 hiprint 格式的字段定义
        const hiprintFields = []
        
        // 处理公司信息字段
        fieldsData.companyFields.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '公司信息'))
        })
        
        // 处理单据主体字段
        fieldsData.headerFields.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '单据主体'))
        })
        
        // 处理单据明细字段
        fieldsData.tableColumns.forEach(field => {
          hiprintFields.push(this.convertToHiprintField(field, '单据明细'))
        })
        
        console.log('[TemplateDesignerV2] Converted fields:', hiprintFields.length)
        
        // 注册到 hiprint
        if (typeof hiprint.addPrintElementTypes === 'function') {
          hiprint.addPrintElementTypes(this.selectedBillType, hiprintFields)
          console.log('[TemplateDesignerV2] Fields registered via addPrintElementTypes')
        }
        
        // 也尝试注册到 PrintElementTypeManager
        if (hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.add === 'function') {
          hiprint.PrintElementTypeManager.add(this.selectedBillType, hiprintFields)
          console.log('[TemplateDesignerV2] Fields registered via PrintElementTypeManager.add')
        }
        
      } catch (error) {
        console.error('[TemplateDesignerV2] registerFieldsToHiprint error:', error)
      }
    },
    
    /**
     * 将后端字段转换为 hiprint 格式
     */
    convertToHiprintField(field, group) {
      const hiprintType = field.hiprintType || 'text'
      const hiprintOptions = field.hiprintOptions ? (typeof field.hiprintOptions === 'string' ? JSON.parse(field.hiprintOptions) : field.hiprintOptions) : {}
      
      return {
        tid: field.fieldCode,
        title: field.fieldName,
        group: group,
        type: hiprintType,
        options: hiprintOptions,
        field: hiprintOptions.field || field.fieldCode,
        testData: hiprintOptions.testData || `@${field.fieldName}`
      }
    },
    
    /**
     * 渲染字段面板 UI
     */
    renderFieldPanelUI(container, fieldsData) {
      console.log('[TemplateDesignerV2] renderFieldPanelUI')
      
      try {
        // 检查 hiprint 是否有内置的字段面板渲染方法
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (hiprint && hiprint.PrintElementTypeManager && typeof hiprint.PrintElementTypeManager.build === 'function') {
          // 使用 hiprint 内置的方法渲染
          hiprint.PrintElementTypeManager.build('.hiprintEpContainer', 'defaultModule')
          console.log('[TemplateDesignerV2] Field panel rendered via PrintElementTypeManager.build')
          return
        }
        
        // 手动渲染字段面板 (如果 hiprint 没有内置方法)
        this.renderCustomFieldPanel(container, fieldsData)
        
      } catch (error) {
        console.error('[TemplateDesignerV2] renderFieldPanelUI error:', error)
        this.renderCustomFieldPanel(container, fieldsData)
      }
    },
    
    /**
     * 自定义渲染字段面板
     */
    renderCustomFieldPanel(container, fieldsData) {
      console.log('[TemplateDesignerV2] renderCustomFieldPanel')
      
      const groups = [
        { name: '公司信息', fields: fieldsData.companyFields },
        { name: '单据主体', fields: fieldsData.headerFields },
        { name: '单据明细', fields: fieldsData.tableColumns }
      ]
      
      // 创建可折叠的分组
      groups.forEach(group => {
        if (!group.fields || group.fields.length === 0) return
        
        const groupDiv = document.createElement('div')
        groupDiv.className = 'field-group'
        groupDiv.style.marginBottom = '10px'
        
        const headerDiv = document.createElement('div')
        headerDiv.className = 'field-group-header'
        headerDiv.style.cssText = 'cursor:pointer;padding:6px 8px;background:#f0f0f0;border:1px solid #e8e8e8;font-weight:bold;font-size:12px;display:flex;justify-content:space-between;align-items:center;'
        headerDiv.innerHTML = `<span>${group.name}</span><span class="toggle-icon">▼</span>`
        
        const contentDiv = document.createElement('div')
        contentDiv.className = 'field-group-content'
        contentDiv.style.cssText = 'border:1px solid #e8e8e8;border-top:none;padding:4px;background:#fff;'
        
        // 添加字段
        group.fields.forEach(field => {
          const fieldItem = document.createElement('div')
          fieldItem.className = 'field-item'
          fieldItem.draggable = true
          fieldItem.style.cssText = 'padding:4px 6px;margin:2px 0;background:#fff;border:1px solid #d9d9d9;border-radius:3px;cursor:grab;font-size:12px;display:flex;align-items:center;gap:6px;transition:all 0.2s;'
          fieldItem.innerHTML = `
            <span style="color:#1890ff;">${field.fieldName}</span>
          `
          
          // 拖拽事件
          fieldItem.addEventListener('dragstart', (e) => {
            e.dataTransfer.setData('text/plain', JSON.stringify({
              fieldCode: field.fieldCode,
              fieldName: field.fieldName,
              hiprintType: field.hiprintType,
              hiprintOptions: field.hiprintOptions
            }))
            fieldItem.style.opacity = '0.5'
          })
          
          fieldItem.addEventListener('dragend', () => {
            fieldItem.style.opacity = '1'
          })
          
          // 双击添加到画布
          fieldItem.addEventListener('dblclick', () => {
            this.addFieldToCanvas(field)
          })
          
          // hover 效果
          fieldItem.addEventListener('mouseenter', () => {
            fieldItem.style.backgroundColor = '#e6f7ff'
            fieldItem.style.borderColor = '#1890ff'
          })
          fieldItem.addEventListener('mouseleave', () => {
            fieldItem.style.backgroundColor = '#fff'
            fieldItem.style.borderColor = '#d9d9d9'
          })
          
          contentDiv.appendChild(fieldItem)
        })
        
        // 折叠/展开
        headerDiv.addEventListener('click', () => {
          const isVisible = contentDiv.style.display !== 'none'
          contentDiv.style.display = isVisible ? 'none' : 'block'
          headerDiv.querySelector('.toggle-icon').textContent = isVisible ? '▶' : '▼'
        })
        
        groupDiv.appendChild(headerDiv)
        groupDiv.appendChild(contentDiv)
        container.appendChild(groupDiv)
      })
      
      console.log('[TemplateDesignerV2] Custom field panel rendered')
    },
    
    /**
     * 渲染简单的字段面板（用于紧急回退）
     */
    renderSimpleFieldPanel(container) {
      const tip = document.createElement('div')
      tip.style.cssText = 'padding:10px;color:#999;font-size:12px;text-align:center;'
      tip.textContent = '字段加载失败，请检查网络连接'
      container.appendChild(tip)
    },
    
    /**
     * 将字段添加到画布
     */
    addFieldToCanvas(field) {
      console.log('[TemplateDesignerV2] addFieldToCanvas:', field)
      
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (!hiprint) {
          this.$message.error('hiprint 未加载')
          return
        }
        
        const hiprintOptions = field.hiprintOptions ? (typeof field.hiprintOptions === 'string' ? JSON.parse(field.hiprintOptions) : field.hiprintOptions) : {}
        
        // 创建新元素的配置
        const newElement = {
          tid: field.fieldCode,
          options: {
            left: 100,
            top: 100,
            width: 100,
            height: 20,
            field: hiprintOptions.field || field.fieldCode,
            title: hiprintOptions.field || field.fieldName,
            testData: hiprintOptions.testData || `@${field.fieldName}`,
            font: 'SimSun',
            fontSize: 12,
            fontWeight: 'normal',
            color: '#000000',
            textAlign: 'left',
            ...hiprintOptions
          }
        }
        
        // 尝试添加到模板
        if (typeof this.template.addElement === 'function') {
          this.template.addElement(newElement)
        } else if (typeof this.template.addPrintElement === 'function') {
          this.template.addPrintElement(newElement)
        } else {
          console.warn('[TemplateDesignerV2] No method to add element to template')
          this.$message.info('请从左侧面板拖拽字段到画布')
        }
        
      } catch (error) {
        console.error('[TemplateDesignerV2] addFieldToCanvas error:', error)
        this.$message.error('添加字段失败')
      }
    },
    
    /**
     * 设置元素选择监听
     */
    setupElementSelection() {
      if (!this.template) return
      
      try {
        // 尝试监听选中事件
        if (this.template.on) {
          this.template.on('select', () => {
            this.hasSelectedElement = true
          })
          this.template.on('deselect', () => {
            this.hasSelectedElement = false
          })
        }
      } catch (e) {
        console.warn('设置元素监听失败', e)
      }
    },
    
    /**
     * 自动选中第一个元素
     */
    autoSelectFirstElement() {
      console.log('[TemplateDesignerV2] autoSelectFirstElement')
      
      if (!this.template) return
      
      try {
        // 等待一下让DOM渲染
        setTimeout(() => {
          // 尝试查找第一个可编辑元素
          const elements = document.querySelectorAll('#hiprint-printTemplate .hiprint-printElement')
          console.log('[TemplateDesignerV2] found elements:', elements.length)
          
          if (elements.length > 0) {
            // 选中第一个
            const firstElement = elements[0]
            firstElement.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            this.hasSelectedElement = true
          }
        }, 300)
      } catch (error) {
        console.warn('autoSelectFirstElement error:', error)
      }
    },
    
    /**
     * 获取纸张类型key
     */
    getPaperTypeKey(width, height) {
      for (const [key, paper] of Object.entries(this.paperTypes)) {
        if (Math.abs(paper.width - width) < 1 && Math.abs(paper.height - height) < 1) {
          return key
        }
      }
      return 'custom'
    },
    
    /**
     * 设置纸张
     */
    setPaper(type, paper) {
      if (paper) {
        this.curPaper = { type: type, width: paper.width, height: paper.height }
      } else {
        this.curPaper = { type: type, width: this.paperWidth, height: this.paperHeight }
      }
      
      if (this.template && typeof this.template.setPaper === 'function') {
        this.template.setPaper(this.curPaper.width, this.curPaper.height)
      }
    },
    
    /**
     * 显示自定义纸张弹窗
     */
    showCustomPaperModal() {
      this.paperWidth = this.curPaper.width
      this.paperHeight = this.curPaper.height
      this.customPaperModalVisible = true
    },
    
    /**
     * 应用自定义纸张
     */
    applyCustomPaper() {
      if (this.paperWidth && this.paperHeight) {
        this.setPaper('custom', null)
        this.customPaperModalVisible = false
      }
    },
    
    /**
     * 改变缩放
     */
    changeScale(increase) {
      let newScale = this.scaleValue
      if (increase) {
        newScale = Math.min(this.scaleMax, newScale + 0.1)
      } else {
        newScale = Math.max(this.scaleMin, newScale - 0.1)
      }
      
      this.scaleValue = newScale
      
      if (this.template && typeof this.template.zoom === 'function') {
        this.template.zoom(newScale, true)
      }
    },
    
    /**
     * 全选
     */
    selectAll() {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        if (typeof this.template.selectAll === 'function') {
          this.template.selectAll()
        } else if (typeof this.template.selectAllElements === 'function') {
          this.template.selectAllElements()
        } else {
          // 手动选中所有元素
          const elements = document.querySelectorAll('#hiprint-printTemplate .hiprint-printElement')
          elements.forEach((el, index) => {
            if (index === 0) {
              el.dispatchEvent(new MouseEvent('click', { bubbles: true }))
            }
          })
        }
        this.hasSelectedElement = true
      } catch (error) {
        console.warn('全选失败', error)
      }
    },
    
    /**
     * 显示JSON编辑弹窗
     */
    showJsonModal() {
      if (!this.template) return
      
      try {
        // 获取模板 JSON - 尝试多个方法名
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          throw new Error('无法获取模板数据')
        }
        
        // 使用 replacer 处理循环引用
        this.templateJsonText = JSON.stringify(templateJSON, (key, value) => {
          if (key === 'panel' || key === 'parent' || key === 'self' || key === '_parent') {
            return undefined
          }
          return value
        }, this.jsonCompressed ? 0 : 2)
        
        this.jsonModalVisible = true
      } catch (error) {
        console.warn('获取模板JSON失败', error)
        this.$message.error('获取模板JSON失败')
      }
    },
    
    /**
     * 格式化JSON
     */
    formatJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        this.templateJsonText = JSON.stringify(json, null, 2)
        this.jsonCompressed = false
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 压缩JSON
     */
    compressJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        this.templateJsonText = JSON.stringify(json)
        this.jsonCompressed = true
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 应用JSON
     */
    applyJson() {
      try {
        const json = JSON.parse(this.templateJsonText)
        
        // 先销毁旧模板
        if (this.template) {
          try {
            if (this.template.destroy) {
              this.template.destroy()
            }
            this.template = null
          } catch (e) {
            console.warn('销毁旧模板失败', e)
          }
        }
        
        // 清空容器
        const container = document.querySelector('#hiprint-printTemplate')
        if (container) {
          container.innerHTML = ''
        }
        
        // 再创建新模板
        this.initHiprintDesigner(json)
        this.jsonModalVisible = false
        this.$message.success('模板已应用')
      } catch (e) {
        this.$message.error('JSON格式错误')
      }
    },
    
    /**
     * 恢复默认模板
     */
    handleReset() {
      const billTypeName = getBillTypeName(this.selectedBillType)
      const defaultTemplate = getDefaultTemplateByName(billTypeName) || getDefaultTemplateByCode(this.selectedBillType)
      
      if (!defaultTemplate) {
        this.$message.warning('未找到默认模板')
        return
      }
      
      try {
        // 先销毁旧模板
        if (this.template) {
          try {
            if (this.template.destroy) {
              this.template.destroy()
            }
            this.template = null
          } catch (e) {
            console.warn('销毁旧模板失败', e)
          }
        }
        
        // 清空容器
        const container = document.querySelector('#hiprint-printTemplate')
        if (container) {
          container.innerHTML = ''
        }
        
        // 再创建新模板
        this.initHiprintDesigner(defaultTemplate)
        this.currentTemplateName = billTypeName + '（默认）'
        this.$message.success('已恢复默认模板')
      } catch (error) {
        console.error('恢复默认模板失败', error)
        this.$message.error('恢复默认模板失败: ' + error.message)
      }
    },
    
    /**
     * 刷新
     */
    handleRefresh() {
      this.reinitialize()
    },
    
    /**
     * 清空
     */
    handleClear() {
      if (this.template && typeof this.template.clear === 'function') {
        this.template.clear()
        this.hasSelectedElement = false
      }
    },
    
    /**
     * 保存
     */
    async handleSave() {
      if (!this.template) return
      
      try {
        // 获取模板 JSON - 尝试多个方法名
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          throw new Error('无法获取模板数据')
        }

        // 强制缩放为1，保证打印时100%原始大小
        if (templateJSON.panels && templateJSON.panels[0]) {
          templateJSON.panels[0].scale = 1
        }

        // 处理循环引用的 JSON 序列化
        const templateJsonStr = JSON.stringify(templateJSON, (key, value) => {
          // 跳过可能导致循环引用的属性
          if (key === 'panel' || key === 'parent' || key === 'self' || key === '_parent') {
            return undefined
          }
          return value
        })
        
        // 获取 billType 代码和名称
        const billTypeCode = this.selectedBillType || 'QGD'
        const billTypeName = getBillTypeName(billTypeCode)
        
        // 获取当前用户的 tenantId（优先从 Vuex store 获取，回退到 localStorage）
        let currentTenantId = null
        try {
          // 优先从 Vuex store 获取
          const userInfo = this.$store.getters.userInfo || this.$store.state.user.info
          if (userInfo) {
            currentTenantId = userInfo.tenantId
            console.log('[TemplateDesignerV2] 从 Vuex 获取用户信息:', { tenantId: currentTenantId })
          }
          
          // 如果 Vuex 没有，尝试从 localStorage 获取（使用正确的 key）
          if (!currentTenantId) {
            const storedUserInfo = Vue.ls.get('Login_Userinfo')
            if (storedUserInfo) {
              currentTenantId = storedUserInfo.tenantId
              console.log('[TemplateDesignerV2] 从 localStorage 获取用户信息:', { tenantId: currentTenantId })
            }
          }
        } catch (e) {
          console.warn('[TemplateDesignerV2] 获取用户租户ID失败:', e.message)
        }
        console.log('[TemplateDesignerV2] 最终确定用户 tenantId:', currentTenantId)
        
        // 判断是否为系统模板（tenantId 为 null 或 0 表示系统模板，保存时需要新增）
        const isSystemTemplate = (
          this.templateTenantId === 0 || 
          this.templateTenantId === null || 
          this.templateTenantId === undefined
        )
        console.log('[TemplateDesignerV2] 判断模板类型:', {
          templateTenantId: this.templateTenantId,
          isSystemTemplate: isSystemTemplate,
          currentTenantId: currentTenantId
        })
        
        // 构建保存数据（显式传递 tenantId）
        const templateData = {
          templateName: this.currentTemplateName || (billTypeName + '模板'),
          billType: billTypeCode,
          templateJson: templateJsonStr,
          isDefault: 0,
          tenantId: currentTenantId  // 显式传递当前租户ID
        }
        
        // 只有当是租户自己的模板时才传递 id 进行更新
        // 如果是系统模板（tenantId 为 0 或 null），则新增租户模板，不传递 id
        if (!isSystemTemplate && this.selectedTemplate) {
          templateData.id = this.selectedTemplate
          console.log('[TemplateDesignerV2] 更新现有模板，id:', this.selectedTemplate)
        } else {
          console.log('[TemplateDesignerV2] 系统模板或无模板，新增租户模板')
        }
        
        console.log('[TemplateDesignerV2] handleSave templateData:', {
          billType: templateData.billType,
          templateName: templateData.templateName,
          hasId: templateData.id !== undefined,
          tenantId: templateData.tenantId
        })
        
        const result = await saveBillPrintTemplate(templateData)
        
        if (result && result.code === 200) {
          this.$message.success('保存成功')
          
          // 触发保存成功事件
          this.$emit('saved', result.data)
          
          // 更新当前模板名称
          if (result.data && result.data.templateName) {
            this.currentTemplateName = result.data.templateName
          }
          
          // 更新当前模板ID
          if (result.data && result.data.id) {
            this.selectedTemplate = result.data.id
            this.$emit('update:templateId', result.data.id)
          }
          
          // 如果是新增模板（原来是系统模板），更新本地 tenantId
          if (isSystemTemplate && result.data && result.data.tenantId) {
            this.templateTenantId = result.data.tenantId
            console.log('[TemplateDesignerV2] 新增模板后更新 templateTenantId:', this.templateTenantId)
          }
        } else {
          this.$message.error((result && result.msg) || '保存失败')
        }
      } catch (error) {
        console.error('保存失败', error)
        this.$message.error('保存失败: ' + (error.message || '未知错误'))
      }
    },
    
    /**
     * 预览 - 实现内嵌预览功能
     */
    async handlePreview() {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        // 获取模板 JSON - 尝试多个方法名
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          this.$message.error('无法获取模板数据')
          return
        }

        // 强制缩放为1，保证预览时100%真实打印样式
        if (templateJSON.panels && templateJSON.panels[0]) {
          templateJSON.panels[0].scale = 1
        }

        // 调试日志 - 检查 billData
        console.log('[handlePreview] billData prop:', this.billData)
        console.log('[handlePreview] billData type:', typeof this.billData)
        console.log('[handlePreview] billData keys:', this.billData ? Object.keys(this.billData) : 'null/undefined')
        
        // 优先使用真实单据数据，如果没有则使用默认数据
        let previewData
        if (this.billData && typeof this.billData === 'object' && Object.keys(this.billData).length > 0) {
          previewData = this.billData
          console.log('[handlePreview] 使用真实数据')
        } else {
          previewData = this.getDefaultPreviewData()
          console.log('[handlePreview] 使用默认数据 (billData为空)')
        }
        console.log('[handlePreview] previewData:', previewData)
        
        // 使用 HiprintService 渲染（与 BillPrintHiprint 相同的渲染方式）
        let htmlContent = ''
        try {
          if (HiprintService.isAvailable()) {
            htmlContent = HiprintService.renderHtml(templateJSON, previewData)
          }
        } catch (e) {
          console.warn('[handlePreview] HiprintService 渲染失败，尝试直接渲染:', e.message)
        }
        
        // 如果 HiprintService 失败，尝试直接使用 getHtml
        if (!htmlContent) {
          const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
          
          if (hiprint && hiprint.PrintTemplate) {
            // 创建临时模板实例来渲染
            const tempTemplate = new hiprint.PrintTemplate({
              template: templateJSON
            })
            
            // 使用 getHtml 获取 HTML
            if (typeof tempTemplate.getHtml === 'function') {
              const htmlElements = tempTemplate.getHtml(previewData)
              if (htmlElements && htmlElements[0]) {
                htmlContent = htmlElements[0].innerHTML
              }
            }
            
            // 清理临时模板
            if (tempTemplate.destroy) {
              tempTemplate.destroy()
            }
          }
        }
        
        // 设置预览内容
        if (htmlContent) {
          this.previewHtml = htmlContent
        } else {
          this.previewHtml = '<div style="padding:20px;text-align:center;color:#999;">无法生成预览</div>'
        }
        
        console.log('[handlePreview] 渲染完成, htmlContent length:', htmlContent ? htmlContent.length : 0)
        
        // 显示预览对话框
        this.previewVisible = true
        
      } catch (error) {
        console.error('预览失败', error)
        this.$message.error('预览失败: ' + (error.message || '未知错误'))
      }
    },
    
    /**
     * 获取默认预览数据
     */
    getDefaultPreviewData() {
      // 根据不同的单据类型返回默认数据
      const billType = this.selectedBillType
      
      const defaultDataMap = {
        'QGD': {
          billTitle: '请购单',
          number: 'QGD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          address: '示例地址',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CGDD': {
          billTitle: '采购订单',
          number: 'CGDD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          address: '示例地址',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 },
            { rowIndex: 2, name: '商品B', spec: '规格B', unit: '箱', basicNumber: 50, price: 20, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CGRK': {
          billTitle: '采购入库',
          number: 'CGRK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例供应商',
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'XSDD': {
          billTitle: '销售订单',
          number: 'XSDD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例客户',
          remark: '示例备注',
          billDetailAllPrice: '250.00',
          billDetailCnMoney: '贰佰伍拾元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 15, basicTotal: 1500 },
            { rowIndex: 2, name: '商品B', spec: '规格B', unit: '箱', basicNumber: 50, price: 25, basicTotal: 1250 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'XSCK': {
          billTitle: '销售出库',
          number: 'XSCK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          contacts: '示例客户',
          remark: '示例备注',
          billDetailAllPrice: '250.00',
          billDetailCnMoney: '贰佰伍拾元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 15, basicTotal: 1500 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'DBCK': {
          billTitle: '调拨出库',
          number: 'DBCK2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'ZZD': {
          billTitle: '组装单',
          number: 'ZZD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        },
        'CXD': {
          billTitle: '拆卸单',
          number: 'CXD2024001',
          operTimeStr: new Date().toLocaleDateString(),
          remark: '示例备注',
          billDetailAllPrice: '100.00',
          billDetailCnMoney: '壹佰元整',
          creatorName: '制单人',
          billDetails: [
            { rowIndex: 1, name: '商品A', spec: '规格A', unit: '个', basicNumber: 100, price: 10, basicTotal: 1000 }
          ],
          company: { companyName: '示例公司', tel: '1234567890' }
        }
      }
      
      return defaultDataMap[billType] || {
        billTitle: billType + '单据',
        number: billType + '2024001',
        operTimeStr: new Date().toLocaleDateString(),
        remark: '示例备注',
        billDetails: [],
        company: { companyName: '示例公司' }
      }
    },
    
    /**
     * 刷新预览
     */
    refreshPreview() {
      this.handlePreview()
    },
    
    /**
     * 从预览直接打印
     */
    async doPrintFromPreview() {
      if (!this.template) {
        this.$message.warning('设计器未初始化')
        return
      }
      
      try {
        // 获取模板 JSON
        let templateJSON
        if (typeof this.template.getJson === 'function') {
          templateJSON = this.template.getJson()
        } else if (typeof this.template.getJSON === 'function') {
          templateJSON = this.template.getJSON()
        } else {
          this.$message.error('无法获取模板数据')
          return
        }
        
        // 使用默认数据
        const defaultData = this.getDefaultPreviewData()
        
        // 尝试使用 hiprint 直接打印
        const hiprint = window.hiprint || (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint)
        
        if (hiprint && hiprint.PrintTemplate) {
          const tempTemplate = new hiprint.PrintTemplate({
            template: templateJSON
          })
          
          if (typeof tempTemplate.print === 'function') {
            tempTemplate.print(defaultData)
            this.$message.success('已发送打印任务')
          } else {
            // 回退方案：打开打印窗口
            this.printViaWindow()
          }
          
          // 清理临时模板
          if (tempTemplate.destroy) {
            tempTemplate.destroy()
          }
        } else {
          this.printViaWindow()
        }
        
        // 关闭预览对话框
        this.previewVisible = false
        
      } catch (error) {
        console.error('打印失败', error)
        this.$message.error('打印失败: ' + (error.message || '未知错误'))
      }
    },
    
    /**
     * 通过窗口打印（回退方案）
     */
    printViaWindow() {
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.$message.error('无法打开打印窗口，请允许弹出窗口')
        return
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>打印预览</title>
          <style>
            body { margin: 0; padding: 20px; font-family: SimSun, 'Microsoft YaHei', sans-serif; }
            @media print {
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          ${this.previewHtml}
        </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      
      setTimeout(() => {
        printWindow.print()
      }, 500)
    },
    
    /**
     * 返回
     */
    handleBack() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.template-designer-v2 {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
  overflow: hidden;
}

.template-designer-v2.embedded-mode {
  height: 100%;
}

/* 工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  white-space: nowrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.toolbar-center {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: none;
  flex-shrink: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.zoom-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scale-display {
  min-width: 48px;
  text-align: center;
  font-size: 13px;
  color: #333;
}

/* 主内容区 */
.main-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* 左侧面板 */
.left-panel {
  width: 200px;
  background: #fff;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-header {
  padding: 10px 12px;
  font-weight: bold;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.hiprintEpContainer {
  /* hiprint 自动填充 */
}

.loading-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #999;
  font-size: 12px;
}

/* 中间画布区 */
.center-panel {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: #e8e8e8;
  position: relative;
}

.ruler {
  height: 20px;
  background: #f0f0f0;
  border-bottom: 1px solid #d9d9d9;
  margin-bottom: 10px;
}

.hiprint-printTemplate {
  background: #fff;
  min-height: 500px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
}

.empty-state {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* 右侧面板 */
.right-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.PrintElementOptionSetting {
  /* hiprint 自动填充 */
}

.selection-hint {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 12px;
  background: #fafafa;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
}

/* 自定义纸张表单 */
.custom-paper-form {
  padding: 10px 0;
}

.custom-paper-form .form-item {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.custom-paper-form .form-item label {
  width: 100px;
  margin-right: 12px;
  color: #666;
}

.custom-paper-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

/* JSON 弹窗 */
.json-modal-content {
  display: flex;
  flex-direction: column;
}

.json-toolbar {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.json-modal-footer {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* 预览对话框样式 */
.preview-container {
  display: flex;
  height: 600px;
}

.preview-settings {
  width: 200px;
  background: #f5f5f5;
  padding: 15px;
  border-right: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.preview-settings .settings-section h4 {
  margin: 0;
  font-size: 14px;
  color: #333;
  font-weight: 600;
}

.preview-settings .setting-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.preview-settings .setting-item > span {
  font-size: 12px;
  color: #666;
}

.preview-settings .ant-radio-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.preview-actions {
  margin-top: auto;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.preview-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #e8e8e8;
}

.preview-toolbar {
  padding: 8px 15px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #666;
}

.preview-area {
  flex: 1;
  overflow: auto;
  padding: 20px;
  display: flex;
  justify-content: center;
}

.preview-paper {
  background: white;
  min-width: 210mm;
  min-height: 297mm;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transform-origin: top center;
  padding: 20px;
}
</style>
